<section class="document-events" aria-live="polite">
  <div class="document-events__heading">
    <div>
      <h3>{{ title }}</h3>
      <p *ngIf="view !== 'revisions'">Who performed each document action and when.</p>
      <p *ngIf="view === 'revisions'">Server-captured before and after snapshots, with their recorded scope.</p>
    </div>
    @if (!loading) {
      <div class="d-flex gap-2 flex-wrap justify-content-end">
        <span class="badge bg-secondary-lt" *ngIf="view !== 'revisions'">{{ events.length }} events</span>
        <span class="badge bg-azure-lt" *ngIf="view !== 'activity'">{{ snapshotEvents.length || historyEntries.length }} revisions</span>
      </div>
    }
  </div>

  @if (view !== 'revisions' && loading) {
    <div class="document-events__state">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      Loading activity…
    </div>
  } @else if (view !== 'revisions' && errorMessage) {
    <div class="alert alert-warning mb-0" role="alert">{{ errorMessage }}</div>
  } @else if (view !== 'revisions' && !events.length) {
    <div class="document-events__state">No recorded document events yet.</div>
  } @else if (view !== 'revisions') {
    <ol class="document-events__timeline">
      @for (event of events; track event.id) {
        <li>
          <span class="document-events__marker" aria-hidden="true"></span>
          <div class="document-events__content">
            <div class="document-events__event-row">
              <strong>{{ event.event_label }}</strong>
              <time [attr.datetime]="event.occurred_at">
                {{ event.occurred_at | localDateTime }}
              </time>
            </div>
            <div class="document-events__meta">
              <span><i class="ti ti-user" aria-hidden="true"></i> {{ actor(event) }}</span>
              @if (event.event_sequence) { <span class="badge bg-secondary-lt">#{{ event.event_sequence }}</span> }
              @if (event.actor_user_id) { <span class="text-secondary small" [title]="event.actor_user_id">User: {{ event.actor_user_id }}</span> }
              @if (event.actor_employment_id) { <span class="text-secondary small" [title]="event.actor_employment_id">Employment: {{ event.actor_employment_id }}</span> }
              @if (statusTransition(event)) {
                <span class="badge bg-azure-lt text-capitalize">{{ statusTransition(event) }}</span>
              }
              @if (event.related_document_reference) {
                <span>
                  <i class="ti ti-link" aria-hidden="true"></i>
                  {{ humanize(event.related_document_type || 'related document') }}
                  {{ event.related_document_reference }}
                </span>
              }
            </div>
            @if (event.notes) {
              <p class="document-events__notes">{{ event.notes }}</p>
            }
            @if (event.before_snapshot) {
              <details class="mt-2"><summary>Record before this action ({{ event.snapshot_scope || 'record' }})</summary>
                <pre class="bg-body-tertiary p-2 mt-2" tabindex="0" aria-label="Pre-change snapshot JSON">{{ event.before_snapshot | json }}</pre>
              </details>
            } @else {
              <p class="text-secondary small mt-2 mb-0">{{ event.snapshot_state === 'created' ? 'Record created; there was no previous record.' : 'Pre-change snapshot unavailable for this event.' }}</p>
            }
          </div>
        </li>
      }
    </ol>
  }

  @if (view !== 'activity' && snapshotEvents.length) {
    <div class="document-events__revisions">
      <h4>Audited document revisions</h4>
      @for (event of snapshotEvents; track event.id) {
        <details class="mb-3">
          <summary>#{{ event.event_sequence }} · {{ event.event_label }} · {{ actor(event) }} · {{ event.occurred_at | localDateTime }}</summary>
          <p class="text-secondary small mt-2">Snapshot scope: {{ event.snapshot_scope }}. Registry snapshots do not include owner lines.</p>
          @if (event.before_snapshot) {
            <h5>Before change</h5><pre class="bg-body-tertiary p-2" tabindex="0" aria-label="Pre-change snapshot JSON">{{ event.before_snapshot | json }}</pre>
          } @else { <p class="text-secondary">Created; no previous record.</p> }
          <h5>After change</h5><pre class="bg-body-tertiary p-2" tabindex="0" aria-label="Post-change snapshot JSON">{{ event.after_snapshot | json }}</pre>
        </details>
      }
    </div>
  } @else if (view !== 'activity' && historyEntries.length) {
    <div class="document-events__revisions">
      <h4>Legacy saved document revisions</h4>
      <p class="text-secondary small">These legacy snapshots were captured after the action; they are not pre-change audit evidence.</p>
      <ol class="document-events__timeline">
        @for (revision of historyEntries; track revision.event_id || revision.at || $index) {
          <li>
            <span class="document-events__marker document-events__marker--revision" aria-hidden="true"></span>
            <div class="document-events__content">
              <div class="document-events__event-row">
                <strong>{{ historyLabel(revision) }}</strong>
                @if (revision.at) {
                  <time [attr.datetime]="revision.at">{{ revision.at | localDateTime }}</time>
                }
              </div>
              <div class="document-events__meta">
                <span><i class="ti ti-user" aria-hidden="true"></i> {{ historyActor(revision) }}</span>
                @if (revision.actor_employment_id) {
                  <span title="Employment ID"><i class="ti ti-id" aria-hidden="true"></i> {{ revision.actor_employment_id }}</span>
                }
                @if (historyStatus(revision)) {
                  <span class="badge bg-azure-lt text-capitalize">{{ historyStatus(revision) }}</span>
                }
                <span>{{ historyLineCount(revision) }} lines</span>
              </div>
              <details class="document-events__snapshot mt-2">
                <summary>View saved document</summary>
                <div class="document-events__snapshot-card">
                  <div class="document-events__snapshot-header">
                    <div>
                      <div class="text-secondary small">Document</div>
                      <strong>{{ historyDocumentValue(revision, 'reference', 'document_number', 'name', 'id') }}</strong>
                    </div>
                    <span class="badge bg-azure-lt text-capitalize">{{ historyStatus(revision) || 'Saved' }}</span>
                  </div>

                  <div class="row g-3 document-events__snapshot-values">
                    <div class="col-sm-6 col-lg-3"><span>Partner</span><strong>{{ historyDocumentValue(revision, 'vendor_name', 'customer_name', 'partner_display_name') }}</strong></div>
                    <div class="col-sm-6 col-lg-3"><span>Responsible person</span><strong>{{ historyDocumentValue(revision, 'buyer_name', 'owner_name', 'responsible_name') }}</strong></div>
                    <div class="col-sm-6 col-lg-3"><span>Expected date</span><strong>{{ historyDocumentValue(revision, 'expected_arrival', 'expected_date', 'due_date') }}</strong></div>
                    <div class="col-sm-6 col-lg-3"><span>Currency</span><strong>{{ historyCurrency(revision) }}</strong></div>
                  </div>

                  <div class="table-responsive mt-3">
                    <table class="table table-sm table-vcenter mb-0">
                      <thead><tr><th>Product / description</th><th>Variant</th><th class="text-end">Qty</th><th>UoM</th><th class="text-end">Unit price</th><th class="text-end">Tax</th><th class="text-end">Total</th></tr></thead>
                      <tbody>
                        @for (line of historyLines(revision); track line['id'] || $index) {
                          <tr>
                            <td>{{ historyLineValue(line, 'product', 'product_name', 'description', 'name', 'product_id') }}</td>
                            <td>{{ historyLineValue(line, 'variant', 'variant_name', 'product_variant_id') }}</td>
                            <td class="text-end">{{ historyLineAmount(line, 'quantity', 'expected_quantity') | number:'1.0-4' }}</td>
                            <td>{{ historyLineValue(line, 'uom', 'uom_name') }}</td>
                            <td class="text-end">{{ historyLineAmount(line, 'unit_price') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</td>
                            <td class="text-end">{{ historyLineAmount(line, 'tax_amount') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</td>
                            <td class="text-end fw-semibold">{{ historyLineAmount(line, 'total', 'total_amount', 'subtotal') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</td>
                          </tr>
                        } @empty {
                          <tr><td colspan="7" class="text-secondary text-center py-3">No document lines in this revision.</td></tr>
                        }
                      </tbody>
                    </table>
                  </div>

                  <div class="document-events__snapshot-totals">
                    <div><span>Untaxed</span><strong>{{ historyAmount(revision, 'untaxed_total', 'untaxed_amount', 'amount_untaxed') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</strong></div>
                    <div><span>Tax</span><strong>{{ historyAmount(revision, 'tax_total', 'tax_amount', 'amount_tax') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</strong></div>
                    <div><span>Total</span><strong>{{ historyAmount(revision, 'total', 'total_amount', 'amount_total') | currency:historyCurrency(revision):'symbol':'1.2-2' }}</strong></div>
                  </div>

                  @if (historyDocumentValue(revision, 'notes', 'internal_note', 'terms_and_conditions') !== '—') {
                    <div class="border-top pt-3 mt-3"><span class="text-secondary small d-block">Notes</span>{{ historyDocumentValue(revision, 'notes', 'internal_note', 'terms_and_conditions') }}</div>
                  }
                </div>
              </details>
            </div>
          </li>
        }
      </ol>
    </div>
  } @else if (view === 'revisions') {
    <div class="document-events__state">No saved document revisions yet.</div>
  }
</section>
